import { createContext, useContext, useEffect, useState, useCallback } from "react";

const SLIP_KEY = "dimbani_mkeka_v1";
const SlipContext = createContext(null);

const loadSlip = () => {
    try {
        const raw = localStorage.getItem(SLIP_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
    } catch (_) {
        return [];
    }
};

const persistSlip = (picks) => {
    try {
        localStorage.setItem(SLIP_KEY, JSON.stringify(picks));
    } catch (_) {
        // localStorage might be unavailable in private mode — ignore
    }
};

/**
 * Pick shape:
 * {
 *   id: string (matches event_id or `${home}-${away}`),
 *   home_team, away_team,
 *   league_id, league_name_sw, league_flag,
 *   event_id, kickoff_iso, home_logo, away_logo,
 *   choice: "HOME" | "AWAY" | "DRAW"  (default: HOME)
 * }
 */
export const SlipProvider = ({ children }) => {
    const [picks, setPicks] = useState(loadSlip);

    useEffect(() => {
        persistSlip(picks);
    }, [picks]);

    const addPick = useCallback((pick) => {
        setPicks((prev) => {
            if (prev.some((p) => p.id === pick.id)) return prev;
            if (prev.length >= 8) return prev;
            return [...prev, { choice: "HOME", ...pick }];
        });
    }, []);

    const removePick = useCallback((id) => {
        setPicks((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const setChoice = useCallback((id, choice) => {
        setPicks((prev) => prev.map((p) => (p.id === id ? { ...p, choice } : p)));
    }, []);

    const clearSlip = useCallback(() => setPicks([]), []);

    const has = useCallback((id) => picks.some((p) => p.id === id), [picks]);

    return (
        <SlipContext.Provider value={{ picks, addPick, removePick, setChoice, clearSlip, has }}>
            {children}
        </SlipContext.Provider>
    );
};

export const useSlip = () => {
    const ctx = useContext(SlipContext);
    if (!ctx) throw new Error("useSlip must be used within SlipProvider");
    return ctx;
};
